import React, { useEffect, useState } from 'react';
import useScrollLock from '@/hooks/useScrollLock';
import { IoPhonePortrait } from 'react-icons/io5';
import { MdSettingsSuggest } from 'react-icons/md';
import { GiOnTarget } from 'react-icons/gi';
import { FaHandPointRight } from 'react-icons/fa';
import { MdDelete, MdEdit, MdCheck, MdClose } from 'react-icons/md';
import { SessionData, Task } from './types';
import { warn } from 'node:console';

interface PreSessionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sessionData: SessionData;
  setSessionData: React.Dispatch<React.SetStateAction<SessionData>>;
}

const PreSessionModal: React.FC<PreSessionModalProps> = ({ open, onClose, onConfirm, sessionData, setSessionData }) => {
  const [newTaskText, setNewTaskText] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editTaskText, setEditTaskText] = useState('');

  // Prevent body scroll when modal is open
  useScrollLock(open);

  const handleAddTask = () => {
    if (newTaskText.trim()) {
      const newTask = {
        id: Date.now().toString(),
        title: newTaskText.trim(),
        text: newTaskText.trim(),
        status: 'todo' as const,
        completed: false,
        isCompleted: false, // keep for backward compatibility if needed locally
        createdAt: new Date().toISOString()
      } as unknown as Task;
      const updatedTasks = [...(sessionData.tasks || []), newTask];
      setSessionData({ ...sessionData, tasks: updatedTasks });
      setNewTaskText('');
    }
  };

  const handleEditTask = (index: number) => {
    setEditingIndex(index);
    setEditTaskText(sessionData.tasks?.[index]?.title || sessionData.tasks?.[index]?.text || '');
  };

  const handleSaveEdit = (index: number) => {
    if (editTaskText.trim()) {
      const updatedTasks = [...(sessionData.tasks || [])];
      updatedTasks[index] = {
        ...updatedTasks[index],
        title: editTaskText.trim(),
        text: editTaskText.trim()
      };
      setSessionData({ ...sessionData, tasks: updatedTasks });
      setEditingIndex(null);
      setEditTaskText('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditTaskText('');
  };

  const handleDeleteTask = (index: number) => {
    const updatedTasks = sessionData.tasks?.filter((_, i) => i !== index) || [];
    setSessionData({ ...sessionData, tasks: updatedTasks });
  };

  const handleCancel = () => {
    // Clear tasks when cancel is clicked
    setSessionData({ ...sessionData, tasks: [] });
    setNewTaskText('');
    setEditingIndex(null);
    setEditTaskText('');
    onClose();
  };

  const handleBeginFocus = () => {

    // Save any task that's currently being edited before starting
    if (editingIndex !== null && editTaskText.trim()) {
      const updatedTasks = [...(sessionData.tasks || [])];
      updatedTasks[editingIndex] = {
        ...updatedTasks[editingIndex],
        title: editTaskText.trim(),
        text: editTaskText.trim()
      };
      // Update sessionData with the edited task
      setSessionData(prev => ({ ...prev, tasks: updatedTasks }));
      setEditingIndex(null);
      setEditTaskText('');
    }

    onConfirm();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-50 rounded-full flex items-center justify-center border border-orange-200/50 relative overflow-hidden">
              <div className="absolute inset-0 rounded-full animate-ping opacity-75" style={{ animationDuration: '6s' }}></div>
              <IoPhonePortrait className="text-3xl sm:text-4xl text-orange-500 relative z-10 animate-pulse" style={{ animationDuration: '6s' }} />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h3 className="text-2xl sm:text-3xl font-medium text-gray-900">
              You’re here. Let’s begin.
            </h3>
            <p className="text-sm sm:text-base text-gray-600 font-light leading-relaxed">
              Set your phone on Do Not Disturb and reduce distractions.
            </p>
          </div>

          {/* Tips Sections */}
          <div className="space-y-4 text-left">
            {/* Task List Section - Hidden for now */}
            {/* <div className="bg-white border border-gray-200/60 rounded-xl p-5">
              <h4 className="text-sm font-normal text-gray-700 mb-3 text-center">
                Write down the tasks you want to complete
              </h4>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTaskText}
                    onChange={(e) => setNewTaskText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                    placeholder="Add a task you want to complete..."
                    className="flex-1 px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-sm font-normal"
                  />
                  <button
                    onClick={handleAddTask}
                    className="px-5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
                  >
                    Add Task
                  </button>
                </div>
                {sessionData.tasks && sessionData.tasks.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sessionData.tasks.map((task, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200/60 rounded-lg hover:border-orange-200/60 transition-all"
                      >
                        {editingIndex === index ? (
                          <>
                            <input
                              type="text"
                              value={editTaskText}
                              onChange={(e) => setEditTaskText(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveEdit(index);
                                } else if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="flex-1 px-3 py-1.5 bg-white border border-orange-300 rounded-lg text-sm text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-400 focus:outline-none"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveEdit(index)}
                              className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                              title="Save"
                            >
                              <MdCheck className="text-base" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Cancel"
                            >
                              <MdClose className="text-base" />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-sm text-gray-900">
                              {task.task}
                            </span>
                            <button
                              onClick={() => handleEditTask(index)}
                              className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                              title="Edit task"
                            >
                              <MdEdit className="text-base" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(index)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete task"
                            >
                              <MdDelete className="text-base" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {(!sessionData.tasks || sessionData.tasks.length === 0) && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No tasks yet. Add tasks you want to complete during this session.
                  </p>
                )}
              </div>
            </div> */}

            {/* Before you start note */}
            <div className="text-left">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FaHandPointRight className="text-lg text-orange-500/70" />
                Before you start:
              </h4>
              <ul className="text-sm text-gray-600 space-y-2 font-medium">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500/70 mt-0.5">•</span>
                  <span className='text-sm text-gray-900'>Place your phone out of reach</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500/70 mt-0.5">•</span>
                  <span className='text-sm text-gray-900'>Settle into a quiet space</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleCancel}
              className="flex-1 px-6 py-3 bg-white border border-gray-200/60 hover:border-gray-300/60 text-gray-700 rounded-lg font-normal transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Begin Focus
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreSessionModal;